/**
 * Pinned and Recent, as lists — and the context action ("Pin" / "Unpin") every
 * menu entry offers. A layout shows them in its own idiom (a tab, a panel, a
 * Dock, a quick-access toolbar); these are the shared rows.
 */
import { ArrowDown, ArrowUp, Clock, Star, X } from 'lucide-react'
import { useEffect, useRef, useState, type CSSProperties, type HTMLAttributes, type MouseEvent as ReactMouseEvent, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { tw } from '../../../testHooks'
import { NavIcon } from '../navIcons'
import { S, iconButton } from '../tokens'
import type { RunNavItem } from '../types'
import type { RecentShellEntry, ShellEntry, ShellNavModel } from '../useShellNav'
import { useDragReorder } from './useDragReorder'

function EntryRow({ entry, active, onOpen, trailing, meta, drag }: {
  entry: ShellEntry; active: boolean; onOpen: () => void; trailing?: React.ReactNode; meta?: string
  /** Drag-to-reorder binding (Pinned). */
  drag?: HTMLAttributes<HTMLDivElement> & Record<string, unknown>
}) {
  return (
    <div role="listitem" {...(drag || {})} style={{
      display: 'flex', alignItems: 'center', gap: 2, borderRadius: 8, position: 'relative',
      background: active ? S.surface2 : 'transparent', ...(drag ? { touchAction: 'pan-x' } : {}),
    }}>
      <button
        type="button"
        className="tw-sh-item"
        onClick={onOpen}
        aria-current={active ? 'page' : undefined}
        {...tw('nav', entry.item.obj_name, { type: entry.item.obj_type, selected: active })}
        style={{
          flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, minHeight: 50,
          padding: '6px 10px', border: 'none', borderRadius: 8, background: 'transparent',
          textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', color: S.text,
        }}
      >
        <span aria-hidden style={{
          width: 30, height: 30, borderRadius: 8, flexShrink: 0, background: S.accentWeak, color: S.accent,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <NavIcon node={entry.item} size={16} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span className="tw-sh-clip" style={{ display: 'block', fontSize: 15, fontWeight: 500 }}>{entry.item.title}</span>
          {(entry.pathText || meta) && (
            <span className="tw-sh-clip" style={{ display: 'block', fontSize: 13, color: S.text2 }}>
              {[entry.pathText, meta].filter(Boolean).join(' · ')}
            </span>
          )}
        </span>
      </button>
      {trailing}
    </div>
  )
}

export function PinnedList({ model, onPicked, reorder = true, style }: {
  model: ShellNavModel; onPicked?: () => void; reorder?: boolean; style?: CSSProperties
}) {
  // Drag a row to reorder (a live preview opens the gap); the arrow buttons
  // and the model's movePin stay the keyboard way.
  const listRef = useRef<HTMLDivElement>(null)
  const { bind } = useDragReorder({
    axis: 'y', containerRef: listRef, disabled: !reorder || model.pinned.length < 2,
    onMove: (key, to) => {
      const from = model.pinned.findIndex((e) => e.item.id === key)
      const it = model.pinned[from]?.item
      if (it && from !== to) model.movePin(it, to - from)
    },
  })
  if (!model.pinned.length) {
    return <p style={{ margin: 10, fontSize: 14, color: S.text2, lineHeight: 1.5, ...style }}>
      Nothing pinned yet. Use the star on any screen, or right-click a menu entry.
    </p>
  }
  return (
    <div ref={listRef} role="list" aria-label="Pinned screens"
      title={reorder && model.pinned.length > 1 ? 'Drag to reorder' : undefined}
      style={{ display: 'flex', flexDirection: 'column', gap: 2, ...style }}>
      {model.pinned.map((e, i) => (
        <EntryRow key={e.item.id} entry={e} active={model.current?.id === e.item.id}
          onOpen={() => { model.open(e.item); onPicked?.() }}
          drag={reorder ? bind(e.item.id) as HTMLAttributes<HTMLDivElement> & Record<string, unknown> : undefined}
          trailing={(
            <span data-no-drag="" style={{ display: 'inline-flex', alignItems: 'center', marginRight: 4 }}>
              {reorder && model.pinned.length > 1 && (
                <>
                  <button type="button" className="tw-sh-btn" disabled={i === 0}
                    onClick={() => model.movePin(e.item, -1)} aria-label={`Move ${e.item.title} up`}
                    {...tw('action', 'shell-pin-up', { obj: e.item.obj_name })}
                    style={{ ...iconButton(30), opacity: i === 0 ? 0.35 : 1 }}>
                    <ArrowUp size={14} aria-hidden />
                  </button>
                  <button type="button" className="tw-sh-btn" disabled={i === model.pinned.length - 1}
                    onClick={() => model.movePin(e.item, 1)} aria-label={`Move ${e.item.title} down`}
                    {...tw('action', 'shell-pin-down', { obj: e.item.obj_name })}
                    style={{ ...iconButton(30), opacity: i === model.pinned.length - 1 ? 0.35 : 1 }}>
                    <ArrowDown size={14} aria-hidden />
                  </button>
                </>
              )}
              <button type="button" className="tw-sh-btn" onClick={() => model.togglePin(e.item)}
                aria-label={`Unpin ${e.item.title}`} {...tw('action', 'shell-unpin', { obj: e.item.obj_name })}
                style={iconButton(30)}>
                <X size={14} aria-hidden />
              </button>
            </span>
          )} />
      ))}
    </div>
  )
}

export function timeAgo(at: number, now = Date.now()): string {
  if (!at) return ''
  const s = Math.max(0, Math.round((now - at) / 1000))
  if (s < 60) return 'just now'
  const m = Math.round(s / 60)
  if (m < 60) return `${m} min ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h} h ago`
  const d = Math.round(h / 24)
  return d === 1 ? 'yesterday' : `${d} days ago`
}

export function RecentList({ model, onPicked, style }: {
  model: ShellNavModel; onPicked?: () => void; style?: CSSProperties
}) {
  if (!model.recent.length) {
    return <p style={{ margin: 10, fontSize: 14, color: S.text2, lineHeight: 1.5, ...style }}>Screens you open appear here.</p>
  }
  return (
    <div style={style}>
      <div role="list" aria-label="Recently opened" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {model.recent.map((e: RecentShellEntry) => (
          <EntryRow key={e.item.id} entry={e} active={model.current?.id === e.item.id} meta={timeAgo(e.at)}
            onOpen={() => { model.open(e.item); onPicked?.() }} />
        ))}
      </div>
      <button type="button" className="tw-sh-btn" onClick={model.clearRecent}
        {...tw('action', 'shell-recent-clear')}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, margin: '8px 6px', height: 34, padding: '0 10px',
          border: `1px solid ${S.border}`, borderRadius: S.radius, background: 'transparent',
          color: S.text2, fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit',
        }}>
        <Clock size={14} aria-hidden /> Clear recent
      </button>
    </div>
  )
}

/**
 * The context action on a menu entry: Open / Pin or Unpin. Opened by
 * right-click, the context-menu key or Shift+F10 on the entry.
 */
export function useEntryMenu(model: ShellNavModel) {
  const [menu, setMenu] = useState<{ item: RunNavItem; x: number; y: number; return?: HTMLElement | null } | null>(null)
  const onContextMenu = (item: RunNavItem) => (e: ReactMouseEvent<HTMLElement>) => {
    if (item.obj_type === '-') return
    e.preventDefault()
    setMenu({ item, x: e.clientX, y: e.clientY, return: e.currentTarget })
  }
  const onKeyDown = (item: RunNavItem) => (e: ReactKeyboardEvent<HTMLElement>) => {
    if (item.obj_type === '-') return
    if (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) {
      e.preventDefault()
      const r = e.currentTarget.getBoundingClientRect()
      setMenu({ item, x: r.left + 24, y: r.bottom, return: e.currentTarget })
    }
  }
  const ui = menu ? (
    <EntryMenu model={model} item={menu.item} x={menu.x} y={menu.y}
      onClose={() => { const back = menu.return; setMenu(null); back?.focus() }} />
  ) : null
  return { onContextMenu, onKeyDown, ui }
}

function EntryMenu({ model, item, x, y, onClose }: {
  model: ShellNavModel; item: RunNavItem; x: number; y: number; onClose: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) onClose() }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [onClose])
  const pinned = model.isPinned(item)
  const items = [
    { key: 'open', label: 'Open', run: () => model.open(item) },
    { key: pinned ? 'unpin' : 'pin', label: pinned ? 'Unpin' : 'Pin', run: () => model.togglePin(item) },
  ]
  return (
    <div ref={ref} role="menu" aria-label={item.title}
      onKeyDown={(e) => {
        const els = [...(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') || [])]
        const i = els.indexOf(document.activeElement as HTMLElement)
        if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose() }
        if (e.key === 'ArrowDown') { e.preventDefault(); els[(i + 1) % els.length]?.focus() }
        if (e.key === 'ArrowUp') { e.preventDefault(); els[(i - 1 + els.length) % els.length]?.focus() }
        if (e.key === 'Tab') onClose()
      }}
      style={{
        position: 'fixed', left: Math.min(x, window.innerWidth - 200), top: Math.min(y, window.innerHeight - 110),
        zIndex: 9445, minWidth: 180, padding: 4, borderRadius: 10, background: S.raised,
        border: `1px solid ${S.border}`, boxShadow: '0 12px 30px rgba(0,0,0,0.22)', color: S.text,
      }}>
      {items.map((m) => (
        <button key={m.key} type="button" role="menuitem" className="tw-sh-item"
          {...tw('action', `shell-entry-${m.key}`, { obj: item.obj_name })}
          onClick={() => { m.run(); onClose() }}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, width: '100%', minHeight: 38, padding: '0 10px',
            border: 'none', borderRadius: 7, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit',
            fontSize: 14.5, color: S.text, textAlign: 'left',
          }}>
          {m.key === 'open' ? <NavIcon node={item} size={15} />
            : <Star size={15} aria-hidden fill={pinned ? 'none' : S.star} color={pinned ? 'currentColor' : S.starStroke} />}
          {m.label}
        </button>
      ))}
    </div>
  )
}
